import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import type { Service } from "@oathera/contract";
import {
  Chart,
  Sparkline,
  estimateSuffix,
  measureDescription,
  type ChartData,
  type ChartSeries,
  type Measure,
} from "../../components/index.ts";
import { useStreamView } from "../../state/stream-view.tsx";
import type { MetricSeries } from "../../lib/stream/view-state.ts";
import { GOLDEN_SIGNALS, quantityMeasure, type GoldenSignalSpec } from "./vocab.ts";

/**
 * The golden-signals panel (task 11.5; design 12.2 "golden signals"; 12.3, 12.4).
 *
 * The authoritative golden signals come from the REST snapshot's `golden_signals` — traffic
 * (`rate_per_s`), errors (`error_ratio`), latency p50/p95/p99 and saturation — each a `Quantity`
 * that says whether it is exact or an estimate. They are rendered as tiles, each labelled with its
 * confidence ("est." for a sampled estimate, "(unverifiable)" for a no-data value; design 12.3) and
 * never shown green when unverifiable. A tile shows a text em dash for a null value, never a
 * fabricated zero.
 *
 * Below the tiles, when the live stream carries metric series for THIS service, their recent buckets
 * are drawn with the shared Chart wrapper — which brings its always-present data-table alternative
 * (design 12.4). This is real live data straight from the stream, grouped by metric name; it is not
 * shown unless the stream actually delivered points for the service, so the panel never implies a
 * trend that does not exist. A compact per-tile sparkline is drawn from the same series when a
 * tile's signal has a matching live metric by name.
 */

type GoldenSignals = components["schemas"]["GoldenSignals"];
type Quantity = components["schemas"]["Quantity"];

export interface GoldenSignalsPanelProps {
  readonly service: Service;
  readonly signals: GoldenSignals;
  readonly live: boolean;
}

export function GoldenSignalsPanel(props: GoldenSignalsPanelProps): ReactNode {
  const view = useStreamView().view;
  // The live metric series the stream currently holds for this service (any name/labels).
  const serviceSeries = [...view.metrics.values()].filter((s) => s.service === props.service);

  return (
    <section className="svc-signals" aria-label="Golden signals">
      <h2 className="svc-section__title">Golden signals</h2>
      <div className="svc-signals__grid">
        {GOLDEN_SIGNALS.map((spec) => (
          <SignalTile
            key={spec.key}
            spec={spec}
            quantity={props.signals[spec.key]}
            trend={trendFor(serviceSeries, spec)}
          />
        ))}
      </div>

      {serviceSeries.length > 0 && (
        <LiveSeriesCharts series={serviceSeries} live={props.live} />
      )}
    </section>
  );
}

function SignalTile(props: {
  spec: GoldenSignalSpec;
  quantity: Quantity;
  trend: readonly number[];
}): ReactNode {
  const { spec, quantity } = props;
  const measure: Measure = quantityMeasure(quantity);
  const value = quantity.value;

  const display =
    value === null ? "n/a" : `${spec.format(value)}${spec.unit}${estimateSuffix(measure)}`;
  const a11y =
    value === null
      ? `${spec.title}: no data (unverifiable).`
      : `${spec.title}: ${spec.format(value)}${spec.unit}, ${measureDescription(measure)}.`;

  return (
    <div className="svc-tile" aria-label={a11y}>
      <span className="svc-tile__title">{spec.title}</span>
      <span className="svc-tile__value" data-measure={measure}>
        {display}
      </span>
      {props.trend.length >= 2 && (
        <span className="svc-tile__trend">
          <Sparkline
            values={props.trend}
            label={spec.title}
            unit={spec.unit}
            measure={measure}
            format={spec.format}
            width={96}
            height={22}
          />
        </span>
      )}
    </div>
  );
}

/**
 * Draw each live metric series the stream holds for the service as a Chart. One chart per metric
 * name (series with the same name but different labels are plotted as separate lines), so an
 * operator sees the live movement behind the snapshot numbers with a built-in data table.
 */
function LiveSeriesCharts(props: {
  series: readonly MetricSeries[];
  live: boolean;
}): ReactNode {
  const byName = new Map<string, MetricSeries[]>();
  for (const s of props.series) {
    if (s.points.length < 2) continue;
    const list = byName.get(s.name) ?? [];
    list.push(s);
    byName.set(s.name, list);
  }
  if (byName.size === 0) return null;

  return (
    <div className="svc-signals__charts">
      <h3 className="svc-subsection__title">
        Live metrics {props.live ? "" : "(paused)"}
      </h3>
      {[...byName.entries()].map(([name, list]) => {
        const { data, series } = buildAligned(list);
        return (
          <Chart
            key={name}
            title={name}
            data={data}
            series={series}
            x="time"
            xIsMillis
            height={160}
            emptyCause="No live points for this metric in range."
          />
        );
      })}
    </div>
  );
}

/**
 * Align a set of same-named series onto a shared x axis for the Chart wrapper. The union of every
 * series' timestamps is the x column; each series contributes a y column, with a non-finite hole
 * (NaN) wherever it has no point at a given time so the chart/data-table shows an honest gap rather
 * than inventing a value.
 */
function buildAligned(list: readonly MetricSeries[]): {
  data: ChartData;
  series: readonly ChartSeries[];
} {
  const times = new Set<number>();
  for (const s of list) for (const p of s.points) times.add(p.t);
  const xs = [...times].sort((a, b) => a - b);

  const ys = list.map((s) => {
    const byT = new Map(s.points.map((p) => [p.t, p.v] as const));
    return xs.map((t) => byT.get(t) ?? Number.NaN);
  });

  const series: ChartSeries[] = list.map((s) => ({
    label: labelFor(s),
    // Live metric points are the stored roll-up for the bucket; they are exact counters/gauges.
    measure: "exact",
  }));

  return { data: [xs, ...ys], series };
}

/** A short series label from its distinguishing labels (e.g. a route), falling back to the name. */
function labelFor(s: MetricSeries): string {
  const parts = Object.entries(s.labels).map(([k, v]) => `${k}=${v}`);
  return parts.length > 0 ? parts.join(" ") : s.name;
}

/**
 * A per-tile sparkline trend for a signal, summed per bucket across the service's live series whose
 * metric name contains the signal's key hint. This is a best-effort live affordance: when no series
 * name matches the signal it returns `[]` and no sparkline is drawn (the tile still shows the exact
 * snapshot value). It never invents points.
 */
function trendFor(series: readonly MetricSeries[], spec: GoldenSignalSpec): readonly number[] {
  const hint = SIGNAL_METRIC_HINT[spec.key];
  if (hint === undefined) return [];
  const matching = series.filter((s) => s.name.includes(hint));
  if (matching.length === 0) return [];

  const byBucket = new Map<number, number>();
  for (const s of matching) {
    for (const p of s.points) byBucket.set(p.t, (byBucket.get(p.t) ?? 0) + p.v);
  }
  if (byBucket.size < 2) return [];
  return [...byBucket.keys()].sort((a, b) => a - b).map((t) => byBucket.get(t) ?? 0);
}

/**
 * Substring hints that associate a golden-signal tile with the live metric series that back it, by
 * metric NAME only (no invented catalog). A signal with no obvious live counterpart has no hint and
 * simply shows the snapshot value without a sparkline. Latency percentiles share the latency hint.
 */
const SIGNAL_METRIC_HINT: Partial<Record<GoldenSignalSpec["key"], string>> = {
  rate_per_s: "request",
  error_ratio: "error",
  latency_p50_ms: "latency",
  latency_p95_ms: "latency",
  latency_p99_ms: "latency",
};
